import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Spinner, Alert, Modal, Form } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from '../utils/api';
import { toast } from 'react-toastify';
import { API_BASE } from '../utils/env';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQrcode, faFolderOpen, faEdit, faTrash, faUpload, faFile, faFolder, faDownload } from '@fortawesome/free-solid-svg-icons';
import logo from '../imgs/temporary-logo-3.png';

const UserDashboard = () => {
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [editingPdf, setEditingPdf] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    name: '',
    description: '',
    privacy: 'Public',
    password: '',
    allowDownload: true,
    allowShare: true,
    backgroundColor: '#00B2A9',
    backgroundImage: ''
  });
  const [editForm, setEditForm] = useState({
    name: '',
    description: '',
    privacy: 'Public',
    password: '',
    allowDownload: true,
    allowShare: true,
    backgroundColor: '#00B2A9',
    backgroundImage: ''
  });
  const [uploadBgFile, setUploadBgFile] = useState(null);
  const [editBgFile, setEditBgFile] = useState(null);
  const [editSelectedFile, setEditSelectedFile] = useState(null);
  const [confirmingDeleteId, setConfirmingDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // QR Code modal state
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrCodeImage, setQrCodeImage] = useState('');
  const [qrCodePdfId, setQrCodePdfId] = useState(null);
  const [qrCodeLoading, setQrCodeLoading] = useState(false);

  useEffect(() => {
    fetchMyPdfs();
  }, []);

  const fetchMyPdfs = async () => {
    try {
      const response = await axios.get('/api/pdf/my-pdfs');
      setPdfs(response.data);
    } catch (err) {
      setError('Erro ao carregar seus PDFs');
    } finally {
      setLoading(false);
    }
  };

  const onDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setSelectedFile(file);
      setUploadForm(prev => ({
        ...prev,
        name: file.name.replace('.pdf', '')
      }));
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf']
    },
    maxFiles: 1,
    maxSize: 1073741824 // 1GB
  });

  const onEditDrop = (acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      setEditSelectedFile(file);
    }
  };

  const {
    getRootProps: getEditRootProps,
    getInputProps: getEditInputProps,
    isDragActive: isEditDragActive
  } = useDropzone({
    onDrop: onEditDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    maxSize: 1073741824 // 1GB
  });

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    try {
      const MAX_BYTES = 1073741824; // 1GB
      if (selectedFile.size > MAX_BYTES) {
        toast.error('Arquivo muito grande. Tamanho máximo permitido: 1GB');
        setUploading(false);
        return;
      }
      const formData = new FormData();
      formData.append('file', selectedFile);
      if (uploadBgFile) {
        formData.append('backgroundFile', uploadBgFile);
      }
      Object.keys(uploadForm).forEach(key => {
        formData.append(key, uploadForm[key]);
      });

      await axios.post('/api/pdf/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 0,
        onUploadProgress: (progressEvent) => {
          const percent = Math.round((progressEvent.loaded * 100) / (progressEvent.total || selectedFile.size));
          console.debug('Upload progress:', percent + '%');
        }
      });

      toast.success('PDF enviado com sucesso!');
      setShowUploadModal(false);
      setSelectedFile(null);
      setUploadForm({
        name: '',
        description: '',
        privacy: 'Public',
        password: '',
        allowDownload: true,
        allowShare: true,
        backgroundColor: '#00B2A9',
        backgroundImage: ''
      });
      setUploadBgFile(null);
      fetchMyPdfs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao enviar PDF');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (pdf) => {
    setEditingPdf(pdf);
    setEditForm({
      name: pdf.name,
      description: pdf.description || '',
      privacy: pdf.privacy,
      password: pdf.password || '',
      allowDownload: pdf.allowDownload,
      allowShare: pdf.allowShare,
      backgroundColor: pdf.backgroundColor || '#00B2A9',
      backgroundImage: pdf.backgroundImage || ''
    });
    setEditSelectedFile(null);
    setShowEditModal(true);
  };

  const handleUpdate = async () => {
    if (!editingPdf) return;

    setUpdating(true);
    try {
      const formData = new FormData();
      if (editSelectedFile) {
        formData.append('File', editSelectedFile);
      }
      if (editBgFile) {
        formData.append('backgroundFile', editBgFile);
      }
      Object.keys(editForm).forEach(key => {
        const value = editForm[key];
        if (value !== undefined && value !== null) {
          formData.append(key, value);
        }
      });

      await axios.put(`/api/pdf/${editingPdf.id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('PDF atualizado com sucesso!');
      setShowEditModal(false);
      setEditingPdf(null);
      setEditSelectedFile(null);
      setEditForm({
        name: '',
        description: '',
        privacy: 'Public',
        password: '',
        allowDownload: true,
        allowShare: true,
        backgroundColor: '#00B2A9',
        backgroundImage: ''
      });
      setEditBgFile(null);
      fetchMyPdfs();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao atualizar PDF');
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async (id) => {
    setConfirmingDeleteId(id);
  };

  const confirmDelete = async () => {
    if (!confirmingDeleteId) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/pdf/${confirmingDeleteId}`);
      toast.success('PDF excluído com sucesso!');
      setConfirmingDeleteId(null);
      fetchMyPdfs();
    } catch (err) {
      toast.error('Erro ao excluir PDF');
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadQRCode = async (pdfId) => {
    try {
      setQrCodeLoading(true);
      setQrCodePdfId(pdfId);
      setShowQRModal(true);
      
      // Primeiro, gera o QR code com logo
      await axios.post(`/api/qrcode/${pdfId}/generate`, {
        includeLogo: true,
        logoPath: logo
      });
      
      // Depois, obtém a imagem do QR code
      const imageUrl = `${API_BASE}/api/qrcode/${pdfId}/image`;
      setQrCodeImage(imageUrl);
      
    } catch (error) {
      console.error('Erro ao gerar QR code:', error);
      toast.error('Erro ao gerar QR code');
      setShowQRModal(false);
    } finally {
      setQrCodeLoading(false);
    }
  };

  const closeQRModal = () => {
    setShowQRModal(false);
    setQrCodeImage('');
    setQrCodePdfId(null);
    setQrCodeLoading(false);
  };

  const downloadQRCode = () => {
    if (qrCodePdfId) {
      window.open(`${API_BASE}/api/qrcode/${qrCodePdfId}/download`, '_blank');
    }
  };

  const cancelDelete = () => {
    setConfirmingDeleteId(null);
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  if (loading) {
    return (
      <div className="min-vh-100" style={{ backgroundColor: '#f1f5f9' }}>
        <Container className="py-5">
          <div className="text-center" style={{ marginTop: '10rem' }}>
            <div 
              className="d-inline-flex align-items-center justify-content-center mb-4"
              style={{
                width: '80px',
                height: '80px',
                backgroundColor: '#00B2A9',
                borderRadius: '20px',
                fontSize: '2rem',
                color: 'white'
              }}
            >
              <FontAwesomeIcon icon={faFolder} />
            </div>
            <div className="mb-4">
              <Spinner 
                animation="border" 
                role="status" 
                style={{ color: '#00B2A9', width: '3rem', height: '3rem' }}
              >
                <span className="visually-hidden">Carregando...</span>
              </Spinner>
            </div>
            <h3 className="mb-2" style={{ color: '#002F6C' }}>Carregando seus PDFs...</h3>
            <p style={{ color: '#64748b' }}>Aguarde enquanto buscamos seus documentos</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f1f5f9' }}>
      {/* Header Section */}
      <Container className="py-5">
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <h1 className="display-5 fw-bold mb-2" style={{ color: '#002F6C' }}>
                  Meus PDFs
                </h1>
                <p className="lead mb-3" style={{ color: '#64748b' }}>
                  Gerencie seus documentos e visualizações
                </p>
                {pdfs.length > 0 && (
                  <div className="d-flex align-items-center">
                    <div 
                      className="me-2"
                      style={{
                        width: '8px',
                        height: '8px',
                        backgroundColor: '#00B2A9',
                        borderRadius: '50%'
                      }}
                    ></div>
                    <span className="text-muted">
                      {pdfs.length} documento{pdfs.length !== 1 ? 's' : ''} {pdfs.length !== 1 ? 'disponíveis' : 'disponível'}
                    </span>
                  </div>
                )}
              </div>
              
              <Button
                size="lg"
                onClick={() => setShowUploadModal(true)}
                className="fw-semibold px-4 py-3 shadow-lg"
                style={{ 
                  backgroundColor: '#00B2A9', 
                  borderColor: '#00B2A9',
                  borderRadius: '12px',
                  fontSize: '1.1rem',
                  boxShadow: '0 8px 25px rgba(0, 178, 169, 0.3)',
                  transition: 'all 0.3s ease',
                  border: '2px solid #00B2A9'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 12px 35px rgba(0, 178, 169, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 178, 169, 0.3)';
                }}
              >
                + Enviar PDF
              </Button>
            </div>
          </Col>
        </Row>
      </Container>

      <Container className="pb-4">
        {error && (
          <Alert 
            variant="danger" 
            className="mb-4 border-0"
            style={{ 
              backgroundColor: '#fef2f2', 
              color: '#dc2626',
              borderRadius: '12px'
            }}
          >
            {error}
          </Alert>
        )}

        {pdfs.length === 0 ? (
          <Row className="justify-content-center">
            <Col lg={8}>
              <div 
                className="text-center py-5 rounded-3"
                style={{ backgroundColor: '#ffffff', border: '1px solid #e2e8f0' }}
              >
                <div className="mb-4" style={{ fontSize: '5rem', color: '#00B2A9' }}>
                  <FontAwesomeIcon icon={faFolder} />
                </div>
                <h3 className="mb-3" style={{ color: '#002F6C' }}>Nenhum PDF enviado</h3>
                <p className="mb-4" style={{ color: '#64748b' }}>
                  Você ainda não enviou nenhum PDF. Comece enviando seu primeiro documento!
                </p>
                <Button
                  size="lg"
                  onClick={() => setShowUploadModal(true)}
                  className="fw-semibold"
                  style={{ 
                    backgroundColor: '#00B2A9', 
                    borderColor: '#00B2A9',
                    borderRadius: '12px'
                  }}
                >
                  Enviar Primeiro PDF
                </Button>
              </div>
            </Col>
          </Row>
        ) : (
          <>
            <Row>
              {pdfs.map((pdf) => (
                <Col key={pdf.id} md={6} lg={4} xl={3} className="mb-4">
                  <Card 
                    className="h-100 border-0 shadow-lg"
                    style={{ 
                      backgroundColor: '#ffffff',
                      borderRadius: '15px',
                      transition: 'all 0.3s ease',
                      overflow: 'hidden',
                      border: '1px solid #e2e8f0'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-8px)';
                      e.currentTarget.style.boxShadow = '0 10px 40px rgba(0, 178, 169, 0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
                    }}
                  >
                    <div className="position-relative">
                      {pdf.thumbnailPath ? (
                        <Card.Img
                          variant="top"
                          src={`${API_BASE}/api/pdf/${pdf.id}/thumbnail?v=${pdf.sizeBytes || Date.now()}`}
                          style={{ 
                            height: '250px', 
                          
                            filter: 'brightness(0.9)'
                          }}
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center"
                          style={{ 
                            height: '250px',
                            backgroundColor: '#002F6C'
                          }}
                        >
                          <div className="text-center">
                            <div className="mb-2" style={{ fontSize: '3rem', color: '#00B2A9' }}>📄</div>
                            <small style={{ color: '#00B2A9' }}>PDF</small>
                          </div>
                        </div>
                      )}
                      
                      <div 
                        className="position-absolute bottom-0 start-0 w-100"
                        style={{
                          height: '50px',
                          background: 'linear-gradient(transparent, rgba(0, 47, 108, 0.7))'
                        }}
                      ></div>
                      
                      <div className="position-absolute top-0 end-0 m-2">
                        <span 
                          className={`badge px-2 py-1 small ${
                            pdf.privacy === 'Public' 
                              ? 'bg-success' 
                              : pdf.privacy === 'Private' 
                                ? 'bg-danger' 
                                : 'bg-warning'
                          }`}
                          style={{ borderRadius: '8px' }}
                        >
                          {pdf.privacy === 'Public' ? 'Público' : 
                           pdf.privacy === 'Private' ? 'Privado' : 'Protegido'}
                        </span>
                      </div>
                    </div>
                    
                    <Card.Body className="d-flex flex-column p-3">
                      <Card.Title 
                        className="h6 mb-2 fw-semibold" 
                        style={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: '2.4rem',
                          color: '#002F6C'
                        }}
                      >
                        {pdf.name}
                      </Card.Title>
                      
                      {pdf.description && (
                        <Card.Text 
                          className="small mb-3 flex-grow-1"
                          style={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            fontSize: '0.85rem',
                            color: '#64748b'
                          }}
                        >
                          {pdf.description}
                        </Card.Text>
                      )}
                      
                      <div className="mt-auto">
                        <div className="d-flex justify-content-between align-items-center mb-3 pt-2" style={{ borderTop: '1px solid rgba(0, 178, 169, 0.2)' }}>
                          <small style={{ color: '#00B2A9', fontSize: '0.75rem' }}>
                            {formatFileSize(pdf.sizeBytes)}
                          </small>
                          <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
                            {formatDate(pdf.uploadDate)}
                          </small>
                        </div>
                        
                        <div className="d-flex gap-2">
                          <Button
                            as={Link}
                            to={`/viewer/${pdf.id}`}
                            variant="primary"
                            size="sm"
                            className="me-2 fw-semibold"
                            style={{ 
                              backgroundColor: '#00B2A9', 
                              borderColor: '#00B2A9',
                              borderRadius: '8px',
                              fontSize: '0.85rem'
                            }}
                          >
                            <FontAwesomeIcon icon={faFolderOpen} className="me-1" /> Abrir
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleEdit(pdf)}
                            style={{ 
                              borderColor: 'rgba(100, 116, 139, 0.3)',
                              borderRadius: '8px',
                              width: '40px'
                            }}
                            title="Editar PDF"
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleDelete(pdf.id)}
                            style={{ 
                              borderColor: 'rgba(239, 68, 68, 0.3)',
                              borderRadius: '8px',
                              width: '40px'
                            }}
                            title="Excluir PDF"
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                          <Button
                            variant="outline-light"
                            size="sm"
                            onClick={() => handleDownloadQRCode(pdf.id)}
                            style={{ 
                              borderColor: 'rgba(100, 116, 139, 0.3)',
                              borderRadius: '8px',
                              width: '40px',
                              color: '#000000',
                              transition: 'all 0.3s ease'
                            }}
                            title="Baixar QR Code"
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = '#00B2A9';
                              e.currentTarget.style.borderColor = '#00B2A9';
                              e.currentTarget.style.color = 'white';
                              e.currentTarget.style.transform = 'scale(1.05)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                              e.currentTarget.style.borderColor = 'rgba(100, 116, 139, 0.3)';
                              e.currentTarget.style.color = '#000000';
                              e.currentTarget.style.transform = 'scale(1)';
                            }}
                          >
                            <FontAwesomeIcon icon={faQrcode} />
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>

            <Modal 
              show={!!confirmingDeleteId} 
              onHide={cancelDelete} 
              centered
              contentClassName="border-0"
              style={{ backdropFilter: 'blur(10px)' }}
            >
              <div style={{ backgroundColor: '#ffffff', borderRadius: '15px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                <Modal.Header 
                  closeButton
                  className="border-0 pb-2"
                  style={{ backgroundColor: '#ffffff' }}
                >
                  <Modal.Title className="d-flex align-items-center" style={{ color: '#002F6C' }}>
                    <FontAwesomeIcon icon={faTrash} className="me-2" style={{ color: '#dc3545' }} />
                    Confirmar exclusão
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ backgroundColor: '#ffffff' }}>
                  <p style={{ color: '#64748b' }}>
                    Tem certeza que deseja excluir este PDF? Esta ação não pode ser desfeita.
                  </p>
                </Modal.Body>
                <Modal.Footer className="border-0 pt-2" style={{ backgroundColor: '#ffffff' }}>
                  <Button 
                    variant="outline-secondary" 
                    onClick={cancelDelete} 
                    disabled={deleting}
                    style={{ borderRadius: '8px' }}
                  >
                    Cancelar
                  </Button>
                  <Button 
                    variant="danger" 
                    onClick={confirmDelete} 
                    disabled={deleting}
                    style={{ borderRadius: '8px' }}
                  >
                    {deleting ? 'Excluindo...' : 'Excluir'}
                  </Button>
                </Modal.Footer>
              </div>
            </Modal>
          </>
        )}
      </Container>

      <Modal 
        show={showUploadModal} 
        onHide={() => setShowUploadModal(false)} 
        size="lg"
        centered
        contentClassName="border-0"
        style={{ backdropFilter: 'blur(10px)' }}
      >
        <div style={{ backgroundColor: '#ffffff', borderRadius: '15px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          <Modal.Header 
            closeButton
            className="border-0 pb-2"
            style={{ backgroundColor: '#ffffff' }}
          >
            <Modal.Title className="d-flex align-items-center" style={{ color: '#002F6C' }}>
              <FontAwesomeIcon icon={faUpload} className="me-2" style={{ color: '#00B2A9' }} />
              Enviar PDF
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: '#ffffff' }}>
            <Form>
              <div
                {...getRootProps()}
                className={`border border-2 border-dashed rounded-3 p-4 text-center mb-4 ${
                  isDragActive ? 'border-primary bg-light' : 'border-secondary'
                }`}
                style={{ 
                  borderRadius: '15px',
                  backgroundColor: isDragActive ? '#f0fdfc' : '#f8fafc',
                  borderColor: isDragActive ? '#00B2A9' : 'rgba(100, 116, 139, 0.3)',
                  transition: 'all 0.3s ease'
                }}
              >
                <input {...getInputProps()} />
                {selectedFile ? (
                  <div>
                    <div className="mb-2" style={{ fontSize: '2rem', color: '#00B2A9' }}>
                      <FontAwesomeIcon icon={faFile} />
                    </div>
                    <p className="mb-1 fw-semibold" style={{ color: '#002F6C' }}>{selectedFile.name}</p>
                    <small style={{ color: '#64748b' }}>
                      {formatFileSize(selectedFile.size)}
                    </small>
                  </div>
                ) : (
                  <div>
                    <div className="mb-3" style={{ fontSize: '3rem', color: '#00B2A9' }}>
                      <FontAwesomeIcon icon={faFolder} />
                    </div>
                    <p className="mb-1 fw-semibold" style={{ color: '#002F6C' }}>
                      {isDragActive
                        ? 'Solte o arquivo aqui...'
                        : 'Arraste um PDF aqui ou clique para selecionar'}
                    </p>
                    <small style={{ color: '#64748b' }}>
                      Máximo 1GB
                    </small>
                  </div>
                )}
              </div>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold" style={{ color: '#002F6C' }}>Nome do Documento</Form.Label>
                <Form.Control
                  type="text"
                  value={uploadForm.name}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Digite um nome para o documento"
                  style={{
                    borderRadius: '8px',
                    borderColor: 'rgba(0, 178, 169, 0.3)',
                    backgroundColor: '#f8fafc'
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold" style={{ color: '#002F6C' }}>Descrição (opcional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva o conteúdo do documento"
                  style={{
                    borderRadius: '8px',
                    borderColor: 'rgba(0, 178, 169, 0.3)',
                    backgroundColor: '#f8fafc'
                  }}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ color: '#002F6C' }}>Privacidade</Form.Label>
                    <Form.Select
                      value={uploadForm.privacy}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, privacy: e.target.value }))}
                      style={{
                        borderRadius: '8px',
                        borderColor: 'rgba(0, 178, 169, 0.3)',
                        backgroundColor: '#f8fafc'
                      }}
                    >
                      <option value="Public">Público</option>
                      <option value="PasswordProtected">Protegido por senha</option>
                      <option value="Private">Privado</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  {uploadForm.privacy === 'PasswordProtected' && (
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold" style={{ color: '#002F6C' }}>Senha</Form.Label>
                      <Form.Control
                        type="password"
                        value={uploadForm.password}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Digite uma senha"
                        style={{
                          borderRadius: '8px',
                          borderColor: 'rgba(0, 178, 169, 0.3)',
                          backgroundColor: '#f8fafc'
                        }}
                      />
                    </Form.Group>
                  )}
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Permitir download"
                      checked={uploadForm.allowDownload}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, allowDownload: e.target.checked }))}
                      style={{ color: '#002F6C' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Permitir compartilhamento"
                      checked={uploadForm.allowShare}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, allowShare: e.target.checked }))}
                      style={{ color: '#002F6C' }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold" style={{ color: '#002F6C' }}>Cor de fundo do visualizador</Form.Label>
                <Form.Control
                  type="color"
                  value={uploadForm.backgroundColor}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, backgroundColor: e.target.value }))}
                  style={{
                    borderRadius: '8px',
                    borderColor: 'rgba(0, 178, 169, 0.3)',
                    height: '50px'
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold" style={{ color: '#002F6C' }}>Imagem de fundo (opcional)</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) => setUploadBgFile(e.target.files?.[0] || null)}
                  style={{
                    borderRadius: '8px',
                    borderColor: 'rgba(0, 178, 169, 0.3)',
                    backgroundColor: '#f8fafc'
                  }}
                />
                {uploadBgFile && (
                  <div className="mt-2">
                    <small style={{ color: '#64748b' }}>Selecionado: {uploadBgFile.name}</small>
                  </div>
                )}
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-2" style={{ backgroundColor: '#ffffff' }}>
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowUploadModal(false)}
              style={{ borderRadius: '8px' }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="fw-semibold d-flex align-items-center"
              style={{ 
                backgroundColor: '#00B2A9', 
                borderColor: '#00B2A9',
                borderRadius: '8px'
              }}
            >
              {uploading && (
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
              )}
              {uploading ? 'Enviando...' : 'Enviar PDF'}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal 
        show={showEditModal} 
        onHide={() => setShowEditModal(false)} 
        size="lg"
        centered
        contentClassName="border-0"
        style={{ backdropFilter: 'blur(10px)' }}
      >
        <div style={{ backgroundColor: '#ffffff', borderRadius: '15px', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
          <Modal.Header 
            closeButton
            className="border-0 pb-2"
            style={{ backgroundColor: '#ffffff' }}
          >
            <Modal.Title className="d-flex align-items-center" style={{ color: '#002F6C' }}>
              <span className="me-2">✏️</span>
              Editar PDF
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: '#ffffff' }}>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold" style={{ color: '#002F6C' }}>Nome do Documento</Form.Label>
                <Form.Control
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Digite um nome para o documento"
                  style={{
                    borderRadius: '8px',
                    borderColor: 'rgba(0, 178, 169, 0.3)',
                    backgroundColor: '#f8fafc'
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold" style={{ color: '#002F6C' }}>Descrição (opcional)</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={editForm.description}
                  onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Descreva o conteúdo do documento"
                  style={{
                    borderRadius: '8px',
                    borderColor: 'rgba(0, 178, 169, 0.3)',
                    backgroundColor: '#f8fafc'
                  }}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold" style={{ color: '#002F6C' }}>Privacidade</Form.Label>
                    <Form.Select
                      value={editForm.privacy}
                      onChange={(e) => setEditForm(prev => ({ ...prev, privacy: e.target.value }))}
                      style={{
                        borderRadius: '8px',
                        borderColor: 'rgba(0, 178, 169, 0.3)',
                        backgroundColor: '#f8fafc'
                      }}
                    >
                      <option value="Public">Público</option>
                      <option value="PasswordProtected">Protegido por senha</option>
                      <option value="Private">Privado</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  {editForm.privacy === 'PasswordProtected' && (
                    <Form.Group className="mb-3">
                      <Form.Label className="fw-semibold" style={{ color: '#002F6C' }}>Senha</Form.Label>
                      <Form.Control
                        type="password"
                        value={editForm.password}
                        onChange={(e) => setEditForm(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="Digite uma senha"
                        style={{
                          borderRadius: '8px',
                          borderColor: 'rgba(0, 178, 169, 0.3)',
                          backgroundColor: '#f8fafc'
                        }}
                      />
                    </Form.Group>
                  )}
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Permitir download"
                      checked={editForm.allowDownload}
                      onChange={(e) => setEditForm(prev => ({ ...prev, allowDownload: e.target.checked }))}
                      style={{ color: '#002F6C' }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      label="Permitir compartilhamento"
                      checked={editForm.allowShare}
                      onChange={(e) => setEditForm(prev => ({ ...prev, allowShare: e.target.checked }))}
                      style={{ color: '#002F6C' }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold" style={{ color: '#002F6C' }}>Cor de fundo do visualizador</Form.Label>
                <Form.Control
                  type="color"
                  value={editForm.backgroundColor}
                  onChange={(e) => setEditForm(prev => ({ ...prev, backgroundColor: e.target.value }))}
                  style={{
                    borderRadius: '8px',
                    borderColor: 'rgba(0, 178, 169, 0.3)',
                    height: '50px'
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold" style={{ color: '#002F6C' }}>Imagem de fundo (opcional)</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={(e) => setEditBgFile(e.target.files?.[0] || null)}
                  style={{
                    borderRadius: '8px',
                    borderColor: 'rgba(0, 178, 169, 0.3)',
                    backgroundColor: '#f8fafc'
                  }}
                />
                {editBgFile && (
                  <div className="mt-2">
                    <small style={{ color: '#64748b' }}>Selecionado: {editBgFile.name}</small>
                  </div>
                )}
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold" style={{ color: '#002F6C' }}>Substituir arquivo PDF (opcional)</Form.Label>
                <div
                  {...getEditRootProps()}
                  className={`border border-2 border-dashed rounded-3 p-3 text-center mb-2 ${
                    isEditDragActive ? 'border-primary bg-light' : 'border-secondary'
                  }`}
                  style={{ 
                    borderRadius: '12px',
                    backgroundColor: isEditDragActive ? '#f0fdfc' : '#f8fafc',
                    borderColor: isEditDragActive ? '#00B2A9' : 'rgba(100, 116, 139, 0.3)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <input {...getEditInputProps()} />
                  {editSelectedFile ? (
                    <div>
                      <div className="mb-2" style={{ fontSize: '1.5rem', color: '#00B2A9' }}>📄</div>
                      <p className="mb-1 fw-semibold" style={{ color: '#002F6C' }}>{editSelectedFile.name}</p>
                      <small style={{ color: '#64748b' }}>{formatFileSize(editSelectedFile.size)}</small>
                    </div>
                  ) : (
                    <div>
                      <div className="mb-2" style={{ fontSize: '2rem', color: '#00B2A9' }}>📁</div>
                      <p className="mb-1 fw-semibold" style={{ color: '#002F6C' }}>
                        {isEditDragActive
                          ? 'Solte o arquivo aqui...'
                          : 'Arraste um PDF aqui ou clique para selecionar'}
                      </p>
                      <small style={{ color: '#64748b' }}>Máximo 1GB</small>
                    </div>
                  )}
                </div>
                <Form.Text style={{ color: '#64748b' }}>
                  Se selecionar um novo PDF, o arquivo antigo será excluído e os canvases/thumbnail serão regenerados.
                </Form.Text>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer className="border-0 pt-2" style={{ backgroundColor: '#ffffff' }}>
            <Button 
              variant="outline-secondary" 
              onClick={() => setShowEditModal(false)}
              style={{ borderRadius: '8px' }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={updating}
              className="fw-semibold"
              style={{ 
                backgroundColor: '#00B2A9', 
                borderColor: '#00B2A9',
                borderRadius: '8px'
              }}
            >
              {updating ? 'Atualizando...' : 'Atualizar PDF'}
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      {/* QR Code Modal */}
      <Modal 
        show={showQRModal} 
        onHide={closeQRModal}
        centered
        backdrop="static"
      >
        <div style={{ borderRadius: '12px', overflow: 'hidden' }}>
          <Modal.Header 
            closeButton 
            className="border-0 pb-2" 
            style={{ backgroundColor: '#ffffff' }}
          >
            <Modal.Title className="d-flex align-items-center" style={{ color: '#002F6C' }}>
              <FontAwesomeIcon icon={faQrcode} className="me-2" />
              QR Code
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ backgroundColor: '#ffffff', textAlign: 'center' }}>
            {qrCodeLoading ? (
              <div className="d-flex flex-column align-items-center py-4">
                <Spinner animation="border" style={{ color: '#00B2A9' }} />
                <p className="mt-3 mb-0" style={{ color: '#002F6C' }}>Gerando QR Code...</p>
              </div>
            ) : (
              qrCodeImage && (
                <div className="d-flex flex-column align-items-center">
                  <img 
                    src={qrCodeImage} 
                    alt="QR Code" 
                    style={{ 
                      maxWidth: '250px', 
                      maxHeight: '250px',
                      border: '2px solid #f1f5f9',
                      borderRadius: '8px',
                      padding: '10px',
                      backgroundColor: '#ffffff'
                    }} 
                  />
                  <p className="mt-3 mb-0 small" style={{ color: '#666' }}>
                    Escaneie o QR Code para acessar o documento
                  </p>
                </div>
              )
            )}
          </Modal.Body>
          <Modal.Footer className="border-0 pt-2" style={{ backgroundColor: '#ffffff' }}>
            <Button 
              variant="outline-secondary" 
              onClick={closeQRModal}
              style={{ borderRadius: '8px' }}
            >
              Fechar
            </Button>
            {!qrCodeLoading && qrCodeImage && (
              <Button
                onClick={downloadQRCode}
                style={{
                  backgroundColor: '#00B2A9',
                  borderColor: '#00B2A9',
                  borderRadius: '8px'
                }}
              >
                <FontAwesomeIcon icon={faDownload} className="me-2" />
                Baixar QR Code
              </Button>
            )}
          </Modal.Footer>
        </div>
      </Modal>
    </div>
  );
};

export default UserDashboard;